import React, { FC, useContext, useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { v4 } from 'uuid'
import { uniq } from 'lodash'

import MainModal from './common/MainModal'
import { AddItemSkeleton } from './common/AddItemSkeleton'
import { SelectClasses } from './components/SelectClasses'
import { SelectHorse } from './components/SelectHorse'
import { AddTeamMembers } from './components/AddTeamMembers'
import { ConfirmDetails } from './components/ConfirmDetails'
import { RiderInfoPanel } from './components/RiderInfoPanel'
import { Button } from '../ui/button'

import { HandleModalContext } from '../../layout/mainlayout/MainLayout'
import helpers from '../../commonHelpers/helpers'
import { getUserFullName } from '../../helpers/helpers'
import { getConvertedData } from '../../models/interface.helper'
import { httpService } from '../../services/httpService'
import useToasterHelper from '../../helpers/ToasterHelper'
import { generatePaperworkDocument } from '../../helpers/sign'
import { getFilteredPaperwork } from '../../helpers/documents'

import EventRegisteredUsersService from '../../services/firebase-service/event-registered-users/EventRegisteredUsersService'
import EventReviewPublishService from '../../services/firebase-service/event-review-publish/EventReviewPublishService'
import HorsesService from '../../services/firebase-service/horses/HorsesService'
import UserService from '../../services/firebase-service/users/userService'
import FirestoreService from '../../services/firestoreService'
import RiderTeamMemberService from '../../services/firebase-service/rider-team-member/RiderTeamMemberService'

import { RegistrationByDayModel } from '../../models/registrations-by-day/registrationByDay.model'
import { RiderTeamMemberModel } from '../../models/rider-team-member/riderTeamMember.model'

import { IUserInterface } from '../../models/users/user.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { FeesTypeItem } from '../../models/event-fees/event-fees.interface'
import { IHorseData } from '../../models/horse/horse.interface'
import { TEventRegisteredUsers } from '../../models/event-registered-users/event-registered-users.interface'
import { IRiderTeamMemberInterface } from '../../models/rider-team-member/riderTeamMember.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../types/payment'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export type IRegistrationFees = FeesTypeItem & {
  selected?: boolean
  isQualify?: boolean
  children?: IRegistrationFees[]
}

export type IAddedTeamMembers = {
  id: string
  memberId: string
  memberName: string
  memberPicture: string
  memberDob: string
  memberEmail: string
  memberRole: string
}

interface AddClassesProps {
  dataToPassOn: {
    eventId: string
    registrationDocId: string
    user: IUserInterface
    onSave?: (value: IRegistrationByDayInterface[]) => Promise<void>
  }
}

export const AddClasses: FC<AddClassesProps> = ({ dataToPassOn }) => {
  const toastFunctions = useToasterHelper()

  const { eventId, registrationDocId, user, onSave } = dataToPassOn

  const [buttonLoading, setButtonLoading] = useState('')
  const [loading, setLoading] = useState(false)
  const [classes, setClasses] = useState<IRegistrationFees[]>([])
  const [selectedClasses, setSelectedClasses] = useState<IRegistrationFees[]>([])
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)
  const [step, setStep] = useState(1)
  const [horses, setHorses] = useState<(IHorseData & { selected?: boolean })[]>([])
  const [teamMembers, setTeamMembers] = useState<IUserInterface[]>([])
  const [addedTeamMembers, setAddedTeamMembers] = useState<IAddedTeamMembers[]>([])
  const [eventRegisteredUser, setEventRegisteredUser] = useState<TEventRegisteredUsers | null>(null)

  const handleModal = useContext(HandleModalContext)?.handleModal

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const publishedEvent = await EventReviewPublishService.getById(eventId)
        const registrationInDb = await EventRegisteredUsersService.getById(registrationDocId)
        const horsesInDb = await HorsesService.getHorsesByIds(uniq(registrationInDb.horsesIds))
        const membersInDb = await UserService.getByIds(uniq(registrationInDb.membersIds))

        setClasses(publishedEvent?.EventFees?.registrationFees || [])
        setPublishedEvent(publishedEvent)
        setHorses(horsesInDb)
        setTeamMembers(membersInDb)
        setEventRegisteredUser(registrationInDb)
      } catch (e) {
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [eventId, registrationDocId])

  const onNext = async () => {
    setStep(step + 1)
  }

  const onPrev = async () => {
    setStep(step - 1)
  }

  const onSubmit = async () => {
    let registrationsByDayToCreate: IRegistrationByDayInterface[] = []
    const riderTeamMembersToCreate: IRiderTeamMemberInterface[] = []
    try {
      setButtonLoading('Please wait...')

      const selectedHorses = horses.filter((horse) => horse.selected)

      selectedClasses.forEach((selectedClass) => {
        registrationsByDayToCreate.push(
          getConvertedData({
            ...new RegistrationByDayModel({
              userId: eventRegisteredUser?.userId || null,
              userName: eventRegisteredUser?.userName || null,
              eventId: eventRegisteredUser?.eventId || null,
              eventName: eventRegisteredUser?.eventName || null,

              uuid: selectedClass.uuid,

              riderId: user?.id || null,
              riderDob: user?.userDOB || null,
              riderName: getUserFullName(user),
              riderNameNGram: user?.userNameNGram || [],
              riderEmail: user?.userEmail || null,
              riderProfilePicture: user?.userProfilePicture || null,
              riderCountry: user?.userNationality || null,

              horseId: selectedHorses?.[0]?.id || null,
              horseName: selectedHorses?.[0]?.horseName || null,
              horseProfilePicture: selectedHorses?.[0]?.horseProfilePicture || null,
              horsesSelected: selectedHorses.map((item) => ({
                id: item.id || '',
                name: item.horseName || '',
                picture: item.horseProfilePicture || '',
              })),

              registrationDocId: eventRegisteredUser?.id || null,

              registrationPrice: selectedClass.cost || 0,
              includeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax || false,
              registrationByDayName: selectedClass.name || null,
              isQualify: selectedClass.isQualify || null,
              qualifyFee: selectedClass?.qualifyingClass?.price || null,
              qualifyFeeName: selectedClass?.qualifyingClass?.name || null,

              isDivision: selectedClass.children && selectedClass.children.length > 0,
              divisionUniqId: null,

              divisionUuid:
                publishedEvent?.EventFees?.registrationFees?.find((fee) =>
                  fee.children?.find((child) => child.uuid === selectedClass.uuid)
                )?.uuid || null,

              paymentStatus: PAYMENT_STATUS.DEFAULT,
              paymentType: PAYMENT_TYPE.CASH,

              isScratched: false,
              amountScratched: 0,
              amountRefunded: 0,
              refundDocId: null,
              refundId: null,
              refundStatus: null,

              score: null,
              order: 0,
              backNumber: 0,

              breakTime: publishedEvent?.EventFees?.rideTimeRequirement?.minutes || null,
              uniqId: `${v4()}-${Date.now()}`,

              recipientId: user.id,
              recipientName: getUserFullName(user),
              recipientProfilePicture: user.userProfilePicture || null,
            }),
          })
        )
      })
      registrationsByDayToCreate = registrationsByDayToCreate.filter((item) => !item.isDivision)

      const teamMemberInDb = await RiderTeamMemberService.getByRider(registrationDocId, user.id)

      addedTeamMembers.forEach((teamMember) => {
        registrationsByDayToCreate.forEach((registrationByDay) => {
          selectedHorses.forEach((horse) => {
            const isExist = riderTeamMembersToCreate.find(
              (item) =>
                item.teamMemberId === teamMember.memberId &&
                item.teamMemberRole === teamMember.memberRole &&
                item.riderId === registrationByDay.riderId &&
                item.horseId === horse.id
            )

            const isExistInDb = teamMemberInDb.find(
              (item) =>
                item.teamMemberId === teamMember.memberId &&
                item.teamMemberRole === teamMember.memberRole &&
                item.riderId === registrationByDay.riderId &&
                item.horseId === horse.id &&
                item.registrationDocId === eventRegisteredUser?.id
            )

            if (isExist || isExistInDb) return
            riderTeamMembersToCreate.push(
              getConvertedData({
                ...new RiderTeamMemberModel({
                  riderDob: registrationByDay?.riderDob || null,
                  riderName: registrationByDay?.riderName || null,
                  riderEmail: registrationByDay?.riderEmail || null,
                  riderId: registrationByDay?.riderId || null,
                  riderNameNGram: registrationByDay?.riderNameNGram || [],
                  riderProfilePicture: registrationByDay?.riderProfilePicture || null,
                  riderCountry: registrationByDay?.riderCountry || null,

                  mailLog: [],

                  teamMemberId: teamMember.memberId,
                  teamMemberName: teamMember.memberName,
                  teamMemberProfilePicture: teamMember.memberPicture,
                  teamMemberRole: teamMember.memberRole,
                  teamMemberDob: teamMember.memberDob,
                  teamMemberEmail: teamMember.memberEmail,

                  userId: eventRegisteredUser?.userId || null,
                  eventId: eventRegisteredUser?.eventId || null,
                  eventName: eventRegisteredUser?.eventName || null,
                  registrationDocId: eventRegisteredUser?.id || null,

                  horseId: horse.id || null,
                  horseName: horse.horseName || null,
                  horseProfilePicture: horse.horseProfilePicture || null,

                  modified: new Date().toString(),
                  created: new Date().toString(),
                }),
              })
            )
          })
        })
      })

      await helpers.asyncWhileLoop({
        loopCount: registrationsByDayToCreate.length,
        functionToFire: async (index) => {
          registrationsByDayToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.REGISTRATION_BY_DAY.NAME,
              new RegistrationByDayModel({
                ...registrationsByDayToCreate[index],
              }).toFirestore()
            )
          ).id
        },
      })

      await helpers.asyncWhileLoop({
        loopCount: riderTeamMembersToCreate.length,
        functionToFire: async (index) => {
          riderTeamMembersToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
              new RiderTeamMemberModel({
                ...riderTeamMembersToCreate[index],
              }).toFirestore()
            )
          ).id
        },
      })

      if (publishedEvent?.EventDetails) {
        const filteredPaperworkDocuments = getFilteredPaperwork(
          publishedEvent?.EventPaperwork ?? null
        )

        let count = 0
        for (const document of filteredPaperworkDocuments) {
          for (const teamMember of riderTeamMembersToCreate) {
            await generatePaperworkDocument({
              paperwork: document,
              currRidersTeamMember: teamMember,
              userData: user!,
              eventId: teamMember.eventId || '',
              EventDetails: publishedEvent?.EventDetails,
              dateOfSignature: new Date(),
              isSigned: false,
            })
            setButtonLoading(
              `Please wait... ${count || 0} of ${filteredPaperworkDocuments.length * riderTeamMembersToCreate.length || '...'} ready`
            )
            count++
          }
        }
      }

      await httpService({
        url: 'update_recipient',
        data: { registration: eventRegisteredUser, payer: user },
        method: 'POST',
      })

      if (onSave) await onSave(registrationsByDayToCreate)
      handleModal?.(false, MODAL_CONSTS.ADD_CLASSES)
    } catch (error) {
      // ROLLBACK "REGISTRATION_BY_DAY" if update_recipient failed
      await helpers.asyncWhileLoop({
        loopCount: registrationsByDayToCreate.length,
        functionToFire: async (index) => {
          await FirestoreService.deleteItem(
            COLLECTIONS.REGISTRATION_BY_DAY.NAME,
            registrationsByDayToCreate[index].id!
          )
        },
      })
      // ROLLBACK "RIDER_TEAM_MEMBER" if update_recipient failed
      await helpers.asyncWhileLoop({
        loopCount: riderTeamMembersToCreate.length,
        functionToFire: async (index) => {
          await FirestoreService.deleteItem(
            COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
            riderTeamMembersToCreate[index].id!
          )
        },
      })
      console.error('error:', error)
      toastFunctions.error({
        message: 'Add classes failed',
      })
    } finally {
      setButtonLoading('')
    }
  }

  return (
    <MainModal
      customTitle={
        <h4 className="flex items-center gap-2 text-2xl font-bold">
          {step > 1 && (
            <Button
              variant="link"
              className="w-6 h-6 p-0 rounded-full bg-transparent hover:opacity-70 transition-all"
              onClick={() => setStep(step - 1)}
            >
              <ArrowLeft className="!w-full !h-full" />
            </Button>
          )}{' '}
          Add classes
        </h4>
      }
      show={true}
      type="ADD_CLASSES"
      size="lg"
      className="flex flex-col flex-1"
      buttons={[
        {
          label: buttonLoading || (step === 4 ? 'Add' : 'Next >'),
          fullWidth: true,
          disabled:
            !!buttonLoading ||
            (step === 1 && selectedClasses.length === 0) ||
            (step === 2 && horses.filter((horse) => horse.selected).length === 0) ||
            (step === 3 && !addedTeamMembers.every((item) => item.memberRole && item.memberId)),
          onClick: step === 4 ? onSubmit : onNext,
        },
        {
          label: '< Back',
          fullWidth: true,
          disabled: !!buttonLoading || step === 1,
          onClick: onPrev,
        },
        {
          label: 'Cancel',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitIconThemeColor/50',
          fullWidth: true,
          disabled: !!buttonLoading,
          onClick: () => handleModal?.(false, MODAL_CONSTS.ADD_CLASSES),
        },
      ]}
    >
      <div className="flex flex-col flex-1 w-full pt-4 h-full text-SeabiscuitDark200ThemeColor">
        {loading ? (
          <AddItemSkeleton />
        ) : (
          <>
            <RiderInfoPanel
              user={user}
              countClasses={selectedClasses.length || 0}
              countQualifyClasses={selectedClasses.filter((item) => item.isQualify).length || 0}
            />
            {step === 1 && (
              <SelectClasses
                publishedEvent={publishedEvent}
                classes={classes}
                setClasses={setClasses}
                setSelectedClasses={setSelectedClasses}
              />
            )}
            {step === 2 && (
              <SelectHorse
                horses={horses}
                setHorses={setHorses}
                registrationDocId={registrationDocId}
              />
            )}
            {step === 3 && (
              <AddTeamMembers
                teamMembers={teamMembers}
                user={user}
                addedTeamMembers={addedTeamMembers}
                setAddedTeamMembers={setAddedTeamMembers}
              />
            )}
            {step === 4 && (
              <ConfirmDetails
                publishedEvent={publishedEvent}
                selectedClasses={selectedClasses}
                selectedHorses={horses.filter((horse) => horse.selected)}
                addedTeamMembers={addedTeamMembers}
              />
            )}
          </>
        )}
      </div>
    </MainModal>
  )
}
